import { normalizeBankAccounts, primaryBankFields, getBankAccounts, newBankAccountId } from "../models/company.js";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  increment,
  serverTimestamp,
  query,
  orderBy
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";

const COMPANIES_COLLECTION = "companies";

// In-flight promise tracker to deduplicate parallel reads across mounting components (e.g. Header + Dashboard)
let inFlightCompaniesPromise = null;

/**
 * Fetch all companies. Returns existing company profiles ordered by name.
 * Pure idempotent read - never mutates or auto-seeds documents during reads.
 */
/**
 * Several bank accounts per company. The primary one is mirrored into the legacy single fields
 * (bankName / accountNumber / ifscCode / branchName) so invoices, receipts and backups keep working.
 */
function bankFieldsFor(companyData) {
  if (companyData.bankAccounts === undefined) return {};
  const bankAccounts = normalizeBankAccounts(companyData.bankAccounts);
  return { bankAccounts, ...primaryBankFields(bankAccounts) };
}

export async function getCompanies() {
  if (inFlightCompaniesPromise) {
    return inFlightCompaniesPromise;
  }

  inFlightCompaniesPromise = (async () => {
    const colRef = collection(db, COMPANIES_COLLECTION);
    let snapshot;
    try {
      const q = query(colRef, orderBy("name", "asc"));
      snapshot = await getDocs(q);
    } catch (_) {
      snapshot = await getDocs(colRef);
    }

    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt,
      updatedAt: d.data().updatedAt?.toDate ? d.data().updatedAt.toDate().toISOString() : d.data().updatedAt
    }));
  })().finally(() => {
    inFlightCompaniesPromise = null;
  });

  return inFlightCompaniesPromise;
}

/**
 * Fetch a single company by ID
 */
export async function getCompanyById(id) {
  const docRef = doc(db, COMPANIES_COLLECTION, id);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return {
    id: snap.id,
    ...snap.data()
  };
}

/**
 * Create a new company.
 * If marked default or is the only company, unsets default on other companies.
 */
export async function createCompany(companyData) {
  const colRef = collection(db, COMPANIES_COLLECTION);
  const existing = await getDocs(colRef);
  const isFirst = existing.empty;
  const shouldBeDefault = isFirst || Boolean(companyData.isDefault);

  const cleanData = {
    name: (companyData.name || "").trim(),
    tradeName: (companyData.tradeName || "").trim(),
    gstin: (companyData.gstin || "").trim().toUpperCase(),
    phone: (companyData.phone || "").trim(),
    email: (companyData.email || "").trim().toLowerCase(),
    addressLine1: (companyData.addressLine1 || "").trim(),
    city: (companyData.city || "").trim(),
    state: companyData.state || "Tamil Nadu",
    stateCode: companyData.stateCode || "33",
    pincode: (companyData.pincode || "").trim(),
    bankName: (companyData.bankName || "").trim(),
    accountNumber: (companyData.accountNumber || "").trim(),
    ifscCode: (companyData.ifscCode || "").trim().toUpperCase(),
    branchName: (companyData.branchName || "").trim(),
    ...bankFieldsFor(companyData),
    invoicePrefix: (companyData.invoicePrefix || "").trim(), // Default empty string
    nextInvoiceNumber: Math.max(1, Number(companyData.nextInvoiceNumber || 1)), // Default count from 1
    nonGstInvoicePrefix: (companyData.nonGstInvoicePrefix || "").trim(),
    nonGstNextInvoiceNumber: Math.max(1, Number(companyData.nonGstNextInvoiceNumber || 1)),
    invoiceCopies: Math.max(1, Math.min(3, Number(companyData.invoiceCopies || 2))),
    defaultTemplate: companyData.defaultTemplate || "modern-tax",
    purchaseTemplate: companyData.purchaseTemplate || "compact",
    signatureUrl: companyData.signatureUrl || "",
    logoUrl: companyData.logoUrl || "",
    appShortName: (companyData.appShortName || "").trim().slice(0, 12),
    isDefault: shouldBeDefault,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  if (shouldBeDefault && !isFirst) {
    const batch = writeBatch(db);
    existing.docs.forEach((d) => {
      batch.update(d.ref, { isDefault: false });
    });
    const newDocRef = doc(colRef);
    batch.set(newDocRef, cleanData);
    await batch.commit();
    return newDocRef.id;
  }

  const docRef = await addDoc(colRef, cleanData);
  return docRef.id;
}

/**
 * Update an existing company
 */
export async function updateCompany(id, companyData) {
  const docRef = doc(db, COMPANIES_COLLECTION, id);
  const shouldBeDefault = Boolean(companyData.isDefault);

  const updatePayload = {
    ...(companyData.name !== undefined && { name: companyData.name.trim() }),
    ...(companyData.tradeName !== undefined && { tradeName: companyData.tradeName.trim() }),
    ...(companyData.gstin !== undefined && { gstin: companyData.gstin.trim().toUpperCase() }),
    ...(companyData.phone !== undefined && { phone: companyData.phone.trim() }),
    ...(companyData.email !== undefined && { email: companyData.email.trim().toLowerCase() }),
    ...(companyData.addressLine1 !== undefined && { addressLine1: companyData.addressLine1.trim() }),
    ...(companyData.city !== undefined && { city: companyData.city.trim() }),
    ...(companyData.state !== undefined && { state: companyData.state }),
    ...(companyData.stateCode !== undefined && { stateCode: companyData.stateCode }),
    ...(companyData.pincode !== undefined && { pincode: companyData.pincode.trim() }),
    ...(companyData.bankName !== undefined && { bankName: companyData.bankName.trim() }),
    ...(companyData.accountNumber !== undefined && { accountNumber: companyData.accountNumber.trim() }),
    ...(companyData.ifscCode !== undefined && { ifscCode: companyData.ifscCode.trim().toUpperCase() }),
    ...(companyData.branchName !== undefined && { branchName: companyData.branchName.trim() }),
    ...(companyData.bankAccounts !== undefined && bankFieldsFor(companyData)),
    ...(companyData.invoicePrefix !== undefined && { invoicePrefix: companyData.invoicePrefix.trim() }),
    ...(companyData.nextInvoiceNumber !== undefined && { nextInvoiceNumber: Math.max(1, Number(companyData.nextInvoiceNumber || 1)) }),
    ...(companyData.nonGstInvoicePrefix !== undefined && { nonGstInvoicePrefix: companyData.nonGstInvoicePrefix.trim() }),
    ...(companyData.nonGstNextInvoiceNumber !== undefined && { nonGstNextInvoiceNumber: Math.max(1, Number(companyData.nonGstNextInvoiceNumber || 1)) }),
    ...(companyData.invoiceCopies !== undefined && { invoiceCopies: Math.max(1, Math.min(3, Number(companyData.invoiceCopies || 2))) }),
    ...(companyData.defaultTemplate !== undefined && { defaultTemplate: companyData.defaultTemplate }),
    ...(companyData.purchaseTemplate !== undefined && { purchaseTemplate: companyData.purchaseTemplate }),
    ...(companyData.signatureUrl !== undefined && { signatureUrl: companyData.signatureUrl }),
    ...(companyData.logoUrl !== undefined && { logoUrl: companyData.logoUrl }),
    ...(companyData.appShortName !== undefined && { appShortName: String(companyData.appShortName).trim().slice(0, 12) }),
    ...(companyData.isDefault !== undefined && { isDefault: shouldBeDefault }),
    updatedAt: serverTimestamp()
  };

  if (shouldBeDefault) {
    const batch = writeBatch(db);
    const existing = await getDocs(collection(db, COMPANIES_COLLECTION));
    existing.docs.forEach((d) => {
      if (d.id !== id) {
        batch.update(d.ref, { isDefault: false });
      }
    });
    batch.update(docRef, updatePayload);
    await batch.commit();
    return;
  }

  await updateDoc(docRef, updatePayload);
}

/**
 * Set a specific company as the default company
 */
export async function setDefaultCompany(id) {
  const batch = writeBatch(db);
  const existing = await getDocs(collection(db, COMPANIES_COLLECTION));
  existing.docs.forEach((d) => {
    batch.update(d.ref, { isDefault: d.id === id });
  });
  await batch.commit();
}

/**
 * Atomically increment a company's next invoice number by 1
 * Supports separate sequences for GST vs Non-GST
 */
export async function incrementCompanyInvoiceNumber(id, isNonGst = false) {
  if (!id) return;
  const docRef = doc(db, COMPANIES_COLLECTION, id);
  const fieldToIncrement = isNonGst ? "nonGstNextInvoiceNumber" : "nextInvoiceNumber";
  await updateDoc(docRef, {
    [fieldToIncrement]: increment(1),
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete a company
 */
export async function deleteCompany(id) {
  const docRef = doc(db, COMPANIES_COLLECTION, id);
  await deleteDoc(docRef);
}


/** Adds a bank account to a company (used when importing a statement for an unknown account). */
export async function addBankAccountToCompany(company, account) {
  const existing = getBankAccounts(company);
  const added = { ...account, id: account.id || newBankAccountId(), isPrimary: existing.length === 0 };
  const bankAccounts = normalizeBankAccounts([...existing, added]);
  await updateCompany(company.id, { bankAccounts });
  return { bankAccounts, account: bankAccounts.find((a) => a.id === added.id) };
}
